import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Droplet, Dumbbell, Moon, Brain, Zap } from 'lucide-react';

const MicroRecommendations = ({ recommendations }) => {
  if (!recommendations) return null;

  const categoryConfig = {
    hydration: {
      icon: Droplet,
      color: 'blue',
      title: 'Hydration',
      bgClass: 'bg-blue-50 border-blue-200',
      iconClass: 'text-blue-600'
    },
    activity: {
      icon: Dumbbell,
      color: 'green',
      title: 'Activity',
      bgClass: 'bg-green-50 border-green-200',
      iconClass: 'text-green-600'
    },
    sleep: {
      icon: Moon,
      color: 'purple',
      title: 'Sleep',
      bgClass: 'bg-purple-50 border-purple-200',
      iconClass: 'text-purple-600'
    },
    stress: {
      icon: Brain,
      color: 'pink',
      title: 'Stress Management',
      bgClass: 'bg-pink-50 border-pink-200',
      iconClass: 'text-pink-600'
    }
  };

  const hasRecommendations = Object.values(recommendations).some(
    arr => Array.isArray(arr) && arr.length > 0
  );

  if (!hasRecommendations && !recommendations.priority) {
    return null;
  }

  return (
    <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base sm:text-lg flex items-center text-indigo-900">
          <Zap className="h-5 w-5 mr-2" />
          Your Personalized Action Plan
        </CardTitle>
        <p className="text-xs text-indigo-700 mt-1">
          Evidence-based recommendations tailored to your health profile
        </p>
      </CardHeader>
      <CardContent>
        {/* Priority Recommendation */}
        {recommendations.priority && (
          <div className="mb-4 p-4 bg-gradient-to-r from-amber-100 to-orange-100 border-2 border-amber-300 rounded-lg">
            <div className="flex items-start gap-2">
              <Zap className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-amber-900 uppercase mb-1">
                  Priority Action
                </p>
                <p className="text-sm font-medium text-amber-900">
                  {recommendations.priority.action}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Category Recommendations */}
        <div className="space-y-4">
          {Object.entries(recommendations).map(([category, items]) => {
            if (category === 'priority' || !Array.isArray(items) || items.length === 0) {
              return null;
            }

            const config = categoryConfig[category];
            if (!config) return null;

            const Icon = config.icon;

            return (
              <div key={category} className={`p-4 rounded-lg border-2 ${config.bgClass}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon className={`h-5 w-5 ${config.iconClass}`} />
                  <h4 className="text-sm font-bold text-gray-900">{config.title}</h4>
                </div>
                <ul className="space-y-2">
                  {items.map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-gray-400 flex-shrink-0 mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default MicroRecommendations;
